import { format } from "date-fns";
import type * as google_protobuf_timestamp_pb from "google-protobuf/google/protobuf/timestamp_pb";

export const IOX_DATETIME_FORMAT_TAG_KEY = "iox:datetime_format";

export const IOX_DATETIME_ISO_LOCAL = "iso_local";
export const IOX_DATETIME_US_12H = "us_12h";
export const IOX_DATETIME_BROWSER = "browser";

/** When tenant tag is missing — US 12-hour datetime format (MMM d, yyyy h:mm aa). */
export const IOX_DATETIME_DEFAULT = IOX_DATETIME_US_12H;

export const IOX_DATETIME_PRESET_OPTIONS = [IOX_DATETIME_ISO_LOCAL, IOX_DATETIME_US_12H, IOX_DATETIME_BROWSER] as const;
export type IoxDatetimePreset = (typeof IOX_DATETIME_PRESET_OPTIONS)[number];

const DATEFNS_PATTERN: Record<IoxDatetimePreset, string | null> = {
  [IOX_DATETIME_ISO_LOCAL]: "yyyy-MM-dd HH:mm:ss",
  [IOX_DATETIME_US_12H]: "MMM d, yyyy h:mm aa",
  [IOX_DATETIME_BROWSER]: null,
};

export const IOX_DATETIME_ISO_LOCAL_POLICY: DisplayDateTimePolicy = {
  kind: "date-fns",
  pattern: DATEFNS_PATTERN[IOX_DATETIME_ISO_LOCAL]!,
};

export const IOX_DATETIME_US_12H_POLICY: DisplayDateTimePolicy = {
  kind: "date-fns",
  pattern: DATEFNS_PATTERN[IOX_DATETIME_US_12H]!,
};

const browserIntlFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});

export type DisplayDateTimePolicy =
  | { kind: "intl" }
  | { kind: "date-fns"; pattern: string };

export function normalizeIoxDatetimePreset(raw: string | undefined): IoxDatetimePreset {
  if (raw && (IOX_DATETIME_PRESET_OPTIONS as readonly string[]).includes(raw)) {
    return raw as IoxDatetimePreset;
  }
  return IOX_DATETIME_DEFAULT;
}

export function displayDateTimePolicyFromPreset(preset: IoxDatetimePreset): DisplayDateTimePolicy {
  if (preset === IOX_DATETIME_BROWSER) {
    return { kind: "intl" };
  }
  return { kind: "date-fns", pattern: DATEFNS_PATTERN[preset]! };
}

export function displayDateTimePolicyFromTenantTag(raw: string | undefined): DisplayDateTimePolicy {
  return displayDateTimePolicyFromPreset(normalizeIoxDatetimePreset(raw));
}

function coerceToDate(input: google_protobuf_timestamp_pb.Timestamp | Date | number | undefined): Date | undefined {
  if (input === undefined || input === null) {
    return undefined;
  }
  if (input instanceof Date) {
    return input;
  }
  if (typeof input === "number") {
    if (!input) {
      return undefined;
    }
    const d = new Date(0);
    d.setUTCSeconds(input);
    return d;
  }
  const d = new Date(0);
  d.setUTCSeconds(input.getSeconds());
  return d;
}

export function formatTimestampWithPolicy(
  input: google_protobuf_timestamp_pb.Timestamp | Date | number | undefined,
  policy: DisplayDateTimePolicy,
): string {
  const d = coerceToDate(input);
  if (!d) {
    return "";
  }
  if (policy.kind === "intl") {
    return browserIntlFormatter.format(d);
  }
  return format(d, policy.pattern);
}
